import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiClient } from "../../services/api";
import { DispatcherDayOfWeek, DispatcherRule, SimpleRule } from "../../types";
import ScheduleEditor, {
  validateSchedulesPreSubmit,
} from "./ScheduleEditor";
import styles from "./RuleForm.module.css";

const dayOfWeekSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6),
  z.literal(7),
]);

const targetSchema = z.object({
  simpleRuleId: z.string().min(1, "Seleziona simple"),
  percentage: z.coerce.number().min(0, "≥ 0").max(100, "≤ 100"),
});

const scheduleSchema = z.object({
  dayOfWeek: dayOfWeekSchema,
  startHour: z.coerce.number().int().min(0).max(23),
  endHour: z.coerce.number().int().min(0).max(23),
  targets: z.array(targetSchema),
});

const formSchema = z.object({
  displayName: z
    .string()
    .min(1, "Campo obbligatorio")
    .max(100, "Massimo 100 caratteri"),
  active: z.boolean(),
  timezone: z.string().min(1, "Timezone obbligatorio"),
  selectionStrategy: z.enum(["weighted", "observe", "enforce"]),
  schedules: z.array(scheduleSchema),
  fallbackSimpleRuleId: z.string().optional(),
});

export type DispatcherRuleFormData = z.infer<typeof formSchema>;

export function dispatcherRuleToFormData(
  rule: DispatcherRule
): DispatcherRuleFormData {
  return {
    displayName: rule.displayName,
    active: rule.active,
    timezone: rule.timezone || "Europe/Rome",
    selectionStrategy: rule.selection_strategy ?? "weighted",
    schedules: rule.schedules.map((s) => ({
      dayOfWeek: s.dayOfWeek,
      startHour: s.startHour,
      endHour: s.endHour,
      targets: s.targets.map((t) => ({
        simpleRuleId: t.simpleRuleId,
        percentage: t.percentage,
      })),
    })),
    fallbackSimpleRuleId: rule.fallback?.simpleRuleId ?? "",
  };
}

export function dispatcherFormDataToPayload(
  data: DispatcherRuleFormData,
  opts: { forUpdate?: boolean } = {}
) {
  const { forUpdate = false } = opts;

  const payload: Record<string, unknown> = {
    type: "dispatcher",
    displayName: data.displayName.trim(),
    active: data.active,
    timezone: data.timezone.trim(),
    selection_strategy: data.selectionStrategy,
    schedules: data.schedules.map((s) => ({
      dayOfWeek: s.dayOfWeek,
      startHour: s.startHour,
      endHour: s.endHour,
      targets: s.targets.map((t) => ({
        simpleRuleId: t.simpleRuleId,
        percentage: Number(t.percentage),
      })),
    })),
  };

  if (data.fallbackSimpleRuleId && data.fallbackSimpleRuleId.trim()) {
    payload.fallback = { simpleRuleId: data.fallbackSimpleRuleId };
  } else if (forUpdate) {
    payload.fallback = null;
  }

  return payload;
}

interface Props {
  defaultValues?: DispatcherRuleFormData;
  onSubmit: (data: DispatcherRuleFormData) => Promise<void>;
  isLoading: boolean;
  submitLabel: string;
  submitError?: string | null;
}

export default function DispatcherRuleForm({
  defaultValues,
  onSubmit,
  isLoading,
  submitLabel,
  submitError,
}: Props) {
  const form = useForm<DispatcherRuleFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues ?? {
      displayName: "",
      active: true,
      timezone: "Europe/Rome",
      selectionStrategy: "weighted",
      schedules: [],
      fallbackSimpleRuleId: "",
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = form;

  useEffect(() => {
    if (defaultValues) reset(defaultValues);
  }, [defaultValues, reset]);

  const [simpleRules, setSimpleRules] = useState<SimpleRule[]>([]);
  const [loadingSimple, setLoadingSimple] = useState(true);
  const [loadSimpleError, setLoadSimpleError] = useState<string | null>(null);
  const [activeDay, setActiveDay] = useState<DispatcherDayOfWeek>(1);

  useEffect(() => {
    const load = async () => {
      setLoadingSimple(true);
      setLoadSimpleError(null);
      try {
        const res = await apiClient.get("/v1/lead-dispatch/rules", {
          params: { type: "simple", active: "true", limit: 100 },
        });
        const rules: SimpleRule[] = res.data?.data?.rules ?? [];
        setSimpleRules(rules);
      } catch (e) {
        console.error(e);
        setLoadSimpleError("Impossibile caricare la lista delle simple rule");
      } finally {
        setLoadingSimple(false);
      }
    };
    load();
  }, []);

  const schedules = watch("schedules") ?? [];
  const preSubmitValidation = validateSchedulesPreSubmit(schedules);

  const submitDisabled =
    isLoading || loadingSimple || !preSubmitValidation.ok;

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Box className={styles.section}>
        <Typography variant="subtitle1" className={styles.sectionTitle}>
          Informazioni base
        </Typography>
        <Box className={styles.row}>
          <Controller
            name="displayName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Nome regola"
                required
                fullWidth
                inputProps={{ maxLength: 100 }}
                error={!!errors.displayName}
                helperText={errors.displayName?.message}
              />
            )}
          />
        </Box>
        <Box className={styles.rowSplit}>
          <Controller
            name="active"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch checked={field.value} onChange={field.onChange} />
                }
                label="Regola attiva"
              />
            )}
          />
          <Controller
            name="timezone"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Timezone (IANA)"
                size="small"
                sx={{ minWidth: 220 }}
                error={!!errors.timezone}
                helperText={
                  errors.timezone?.message ??
                  "Default Europe/Rome — modificare solo se necessario"
                }
              />
            )}
          />
        </Box>
        <Box className={styles.row}>
          <Controller
            name="selectionStrategy"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Strategia di selezione"
                fullWidth
                error={!!errors.selectionStrategy}
                helperText={
                  errors.selectionStrategy?.message ??
                  "weighted: estrazione casuale (default). observe: resta weighted ma conteggia e logga lo scarto dal deficit-based. enforce: bilanciamento deterministico — rispetta le % esattamente."
                }
              >
                <MenuItem value="weighted">Weighted (casuale — default)</MenuItem>
                <MenuItem value="observe">Observe (osservazione, nessun impatto)</MenuItem>
                <MenuItem value="enforce">Enforce (deterministico, deficit-based)</MenuItem>
              </TextField>
            )}
          />
        </Box>
      </Box>

      <Divider />

      <Box className={styles.section}>
        <Typography variant="subtitle1" className={styles.sectionTitle}>
          Schedule per giorno
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Per ogni giorno della settimana puoi definire una o più fasce orarie.
          Ogni schedule distribuisce il traffico fra simple rule attive.
        </Typography>

        {loadSimpleError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadSimpleError}
          </Alert>
        )}

        {loadingSimple ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <ScheduleEditor
            form={form}
            simpleRules={simpleRules}
            activeDay={activeDay}
            setActiveDay={setActiveDay}
          />
        )}
      </Box>

      <Divider />

      <Box className={styles.section}>
        <Typography variant="subtitle1" className={styles.sectionTitle}>
          Fallback (opzionale)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Simple rule usata quando nessuno schedule è attivo nell'istante
          corrente. Se omesso, le lead in fuori-schedule vengono <strong>skipped</strong>.
        </Typography>
        <Controller
          name="fallbackSimpleRuleId"
          control={control}
          render={({ field }) => (
            <Autocomplete
              options={simpleRules.filter((r) => r.active)}
              getOptionLabel={(opt) =>
                typeof opt === "string"
                  ? opt
                  : `${opt.displayName} — ${opt.destination.url}`
              }
              value={
                simpleRules.find((r) => r.id === field.value) ?? null
              }
              onChange={(_, val) =>
                field.onChange(typeof val === "string" ? val : val?.id ?? "")
              }
              isOptionEqualToValue={(opt, val) => opt.id === val?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Simple fallback"
                  size="small"
                />
              )}
            />
          )}
        />
      </Box>

      {submitError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {submitError}
        </Alert>
      )}

      {!preSubmitValidation.ok && schedules.length > 0 && (
        <Alert
          severity="warning"
          sx={{ mt: 2 }}
          action={
            preSubmitValidation.offendingDay != null &&
            preSubmitValidation.offendingDay !== activeDay ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => setActiveDay(preSubmitValidation.offendingDay!)}
              >
                Vai
              </Button>
            ) : null
          }
        >
          {preSubmitValidation.reason}
        </Alert>
      )}

      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
        <Button type="submit" variant="contained" disabled={submitDisabled}>
          {isLoading ? "Salvataggio..." : submitLabel}
        </Button>
      </Box>
    </form>
  );
}
